'use client'

import React from 'react'
import type { CtaRowBlock as CtaRowBlockProps } from '@/payload-types'
import { getBlockPadding } from '@/utilities/getBlockPadding'
import { cn } from '@/utilities/ui'
import RichText from '@/components/RichText'
import { CTAButton, type CTAButtonVariant } from '@/components/ui/cta-button'
import { resolveHref } from '@/utilities/resolveHref'
import { mapLegacyAppearance } from '@/fields/link'

type Props = CtaRowBlockProps & {
  disableInnerContainer?: boolean
}

export const CtaRowBlock: React.FC<Props> = ({
  alignment,
  text,
  links,
  paddingTop,
  paddingBottom,
  disableInnerContainer: _disableInnerContainer,
}) => {
  const paddingClasses = getBlockPadding({
    paddingTop: paddingTop ?? true,
    paddingBottom: paddingBottom ?? true,
  })

  // Alignment classes for desktop - mobile always left-aligned
  const effectiveAlignment = alignment ?? 'right'
  const alignmentClasses = {
    left: 'md:items-start',
    center: 'md:items-center',
    right: 'md:items-end',
  }[effectiveAlignment]

  const textAlignClasses = {
    left: 'md:text-left',
    center: 'md:text-center',
    right: 'md:text-right',
  }[effectiveAlignment]

  return (
    <div className={paddingClasses}>
      <div
        className={cn(
          'flex flex-col gap-8 md:gap-11 items-start justify-center',
          alignmentClasses,
        )}
      >
        {/* Optional text */}
        {text && (
          <RichText
            data={text}
            enableGutter={false}
            enableProse={false}
            className={cn(
              'text-black/70 w-full text-left [&_strong]:text-black [&_b]:text-black [&_a]:underline [&_a]:underline-offset-2',
              textAlignClasses,
            )}
          />
        )}

        {/* CTA buttons */}
        {links && links.length > 0 && (
          <div className="flex flex-row gap-4 flex-wrap">
            {links.map((linkItem, index) => {
              const cta = linkItem.cta
              const href = cta ? resolveHref(cta) : null
              if (!cta?.label || !href) return null
              const variant = mapLegacyAppearance(cta.appearance) as CTAButtonVariant
              return (
                <CTAButton
                  key={index}
                  variant={variant}
                  href={href}
                  newTab={cta.newTab ?? false}
                >
                  {cta.label}
                </CTAButton>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
